import { create } from "zustand";
import { persist } from "zustand/middleware";

export type CountryPreference = {
  /**
   * Code ISO du pays.
   *
   * La valeur est null lorsque
   * l’utilisateur choisit
   * « Pays non renseigné ».
   */
  countryCode: string | null;

  /**
   * Nom affiché dans l’interface.
   */
  countryName: string;

  /**
   * Code ISO de la devise utilisée
   * initialement pour afficher les offres.
   *
   * Exemples : XAF, XOF, EUR, CAD.
   */
  currencyCode: string;

  /**
   * Nom lisible de la devise.
   *
   * Exemple : Franc CFA BEAC.
   */
  currencyLabel: string;

  /**
   * Code utilisé par flag-icons.
   *
   * Exemples : cm, sn, fr, eu.
   */
  flagCode: string;

  /**
   * Locale utilisée pour le formatage
   * des nombres et des prix.
   *
   * Exemples : fr-CM, fr-SN, fr-FR.
   */
  locale: string;

  /**
   * Indique que l’utilisateur a choisi
   * volontairement de ne pas renseigner
   * son pays.
   *
   * Dans ce cas :
   * - la devise devient EUR ;
   * - seul le paiement par carte est proposé.
   */
  countryNotProvided: boolean;
};

type CountryPreferenceStore = {
  /**
   * Préférence actuellement enregistrée.
   *
   * null signifie que le dialogue du pays
   * doit encore être présenté.
   */
  preference: CountryPreference | null;

  /**
   * Enregistre ou remplace la préférence.
   */
  setPreference: (preference: CountryPreference) => void;

  /**
   * Supprime la préférence.
   *
   * Cette fonction sera notamment utile
   * pendant les tests.
   */
  clearPreference: () => void;
};

export const useCountryPreferenceStore = create<CountryPreferenceStore>()(
  persist(
    (set) => ({
      preference: null,

      setPreference: (preference) => {
        set({ preference });
      },

      clearPreference: () => {
        set({ preference: null });
      },
    }),
    {
      /**
       * Clé utilisée dans le localStorage
       * de l’application MonCVPro.
       */
      name: "moncvpro-country-preference",

      /**
       * Version du format enregistré.
       *
       * Elle permettra plus tard de migrer
       * proprement les anciennes préférences.
       */
      version: 1,

      /**
       * On enregistre seulement la préférence,
       * pas les fonctions du store.
       */
      partialize: (state) => ({
        preference: state.preference,
      }),
    },
  ),
);
